import { useState } from "react";
import { Card, Metric } from "@/components/common";
import { ArrowUpRight, ArrowDownRight, ShieldCheck, ShieldAlert, Copy, Check } from "lucide-react";

const GOLD_SYMBOL_KEY = "aurum_mt5_symbol";

function CopyField({ label, value, hint }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <button onClick={copy} data-testid={`mt5-copy-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="group flex items-center justify-between rounded-lg border border-[var(--border2)] bg-[var(--surface)] px-3 py-2 text-left hover:border-aurum transition-colors">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
        <div className="tnum text-sm font-semibold text-[var(--text)]">{value}{hint && <span className="ml-1 text-[10px] text-[var(--text-muted)] font-normal">{hint}</span>}</div>
      </div>
      {copied ? <Check size={14} className="text-bull" /> : <Copy size={14} className="text-[var(--text-muted)] group-hover:text-aurum" />}
    </button>
  );
}

function Mt5OrderTicket({ setup, trade, sizing, entryGuard }) {
  const [symbol, setSymbol] = useState(() => localStorage.getItem(GOLD_SYMBOL_KEY) || "XAUUSD");
  const saveSymbol = (v) => { setSymbol(v); localStorage.setItem(GOLD_SYMBOL_KEY, v); };
  const long = setup.direction === "LONG";
  const inZone = entryGuard?.state === "IN ZONE";
  const blocked = entryGuard && (entryGuard.state === "CHASING" || entryGuard.state === "INVALIDATED");
  const side = long ? "BUY" : "SELL";
  const orderType = inZone ? side : `${side} LIMIT`;
  const lots = sizing && !sizing.error ? sizing.position_size_lots : null;

  return (
    <div className="mt-4 rounded-xl border border-[var(--border2)] overflow-hidden" data-testid="mt5-order-ticket">
      <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-elev)] border-b border-[var(--border2)]">
        <div className="text-xs font-bold uppercase tracking-wider text-aurum">Place on MetaTrader 5</div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-[var(--text-muted)]">Your gold symbol</span>
          <input value={symbol} onChange={(e) => saveSymbol(e.target.value.toUpperCase())} data-testid="mt5-symbol-input"
            className="w-24 rounded bg-[var(--surface)] border border-[var(--border2)] px-2 py-1 text-xs tnum outline-none focus:border-aurum" />
        </div>
      </div>
      {blocked ? (
        <div className="p-4 text-sm text-warn flex items-center gap-2" data-testid="mt5-blocked">
          <ShieldAlert size={16} /> Do not place this order yet — {entryGuard.reason}
        </div>
      ) : (
        <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <CopyField label="Symbol" value={symbol} />
          <CopyField label="Order Type" value={orderType} />
          {lots != null && <CopyField label="Volume" value={lots} hint="lots" />}
          <CopyField label={inZone ? "Price (market)" : "Entry Price"} value={trade.preferred_entry?.toFixed(2)} />
          <CopyField label="Stop Loss" value={trade.stop?.toFixed(2)} />
          <CopyField label="Take Profit" value={trade.tp1?.toFixed(2)} hint="TP1" />
        </div>
      )}
      {!blocked && (
        <div className="px-3 pb-3 -mt-1 flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-[var(--text-muted)]">
            {inZone ? "Price is in the zone — a market order is fine." : "Price isn't in the zone yet — use a pending LIMIT order at the entry price."}
            {" "}Set SL &amp; TP exactly as shown; the volume caps your loss at the stop.
          </p>
          <div className="flex gap-1.5 text-[10px] text-[var(--text-muted)]">
            <span>Next TPs:</span>
            <span className="tnum">TP2 {trade.tp2?.toFixed(2)}</span>
            <span className="tnum">TP3 {trade.tp3?.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function TradePlanCard({ setup, trade, sizing, dynamicRisk, entryGuard, capitalGuard }) {
  if (!trade || !setup?.direction) {
    return (
      <Card className="p-6 flex flex-col items-center justify-center text-center" data-testid="trade-plan-empty">
        <div className="text-sm font-semibold text-[var(--text-sec)]">NO VALID SETUP</div>
        <p className="mt-1 text-xs text-[var(--text-muted)] max-w-xs">
          No trade plan is generated because conditions do not currently fit your rules.
          Avoiding poor trades is part of the process.
        </p>
      </Card>
    );
  }
  const long = setup.direction === "LONG";
  const dirColor = long ? "#21C58E" : "#EF6262";
  // ladder ordering by price
  const rows = [
    { label: "TP3", price: trade.tp3, color: "#21C58E", rr: trade.rr3 },
    { label: "TP2", price: trade.tp2, color: "#21C58E", rr: trade.rr2 },
    { label: "TP1", price: trade.tp1, color: "#21C58E", rr: trade.rr1 },
    { label: "Entry", price: trade.preferred_entry, color: "#D6AE57" },
    { label: "Stop", price: trade.stop, color: "#EF6262" },
  ].sort((a, b) => b.price - a.price);

  return (
    <Card className="p-5" data-testid="trade-plan-card">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-wider text-[var(--text-muted)]">Trade Plan · {setup.strategy}</div>
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ color: dirColor, background: `${dirColor}1a`, border: `1px solid ${dirColor}33` }} data-testid="trade-direction">
          {long ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />} Potential {setup.direction}
        </span>
      </div>

      {entryGuard && (() => {
        const st = entryGuard.state;
        const col = st === "IN ZONE" ? "#21C58E" : st === "INVALIDATED" ? "#FF4D67" : "#F5B942";
        return (
          <div className="mb-4 rounded-lg p-3" style={{ background: `${col}12`, border: `1px solid ${col}33` }} data-testid="entry-guard">
            <div className="flex items-center gap-2 text-xs font-bold" style={{ color: col }}>
              {entryGuard.entry_valid ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
              ENTRY GUARD · {st}
            </div>
            <p className="mt-1 text-xs text-[var(--text-sec)]">{entryGuard.reason}</p>
            <div className="mt-1 text-[11px] text-[var(--text-muted)] tnum">
              Live {entryGuard.live_price?.toFixed(2)} · drift {entryGuard.drift >= 0 ? "+" : ""}{entryGuard.drift?.toFixed(2)} from entry
            </div>
          </div>
        );
      })()}

      <div className="space-y-1.5 mb-4">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3">
            <span className="text-xs w-12 font-medium" style={{ color: r.color }}>{r.label}</span>
            <div className="flex-1 h-px" style={{ background: `${r.color}55` }} />
            <span className="tnum text-sm font-semibold text-[var(--text)]">{r.price?.toFixed(2)}</span>
            {r.rr != null && <span className="tnum text-[11px] text-[var(--text-muted)] w-14 text-right">{r.rr}R</span>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-4 border-t border-[var(--border2)]">
        <Metric label="Entry Zone" value={`${trade.entry_zone[0].toFixed(2)}–${trade.entry_zone[1].toFixed(2)}`} />
        <Metric label="Stop Distance" value={`${trade.stop_distance.toFixed(2)}`} sub="USD / oz" />
        {sizing && !sizing.error && (
          <>
            <Metric label="Position Size" value={`${sizing.position_size_lots} lots`} sub={`${sizing.units_ounces} oz`} testId="position-size" color="#D6AE57" />
            <Metric label="Max Planned Loss" value={`$${sizing.max_planned_loss}`} sub={`${sizing.risk_pct}% of $${sizing.account_balance}`} color="#EF6262" testId="max-planned-loss" />
          </>
        )}
        <Metric label="R:R to TP1" value={`1 : ${trade.rr1}`} color={trade.rr1 >= 2 ? "#21C58E" : "#F5B942"} />
        <Metric label="Invalidation" value={trade.structural_invalidation.toFixed(2)} sub="structure break" />
      </div>

      {capitalGuard && capitalGuard.max_loss_usd != null && (
        <div className="mt-4 rounded-lg p-3 text-xs" style={{ background: "rgba(33,197,142,0.07)", border: "1px solid rgba(33,197,142,0.22)" }} data-testid="capital-guard">
          <div className="font-semibold text-bull mb-0.5 flex items-center gap-1.5"><ShieldCheck size={13} /> Capital protection</div>
          <p className="text-[var(--text-sec)]">
            Worst case at the stop: <span className="tnum font-semibold text-[var(--text)]">${capitalGuard.max_loss_usd}</span>
            {capitalGuard.max_loss_pct != null && <> (<span className="tnum">{capitalGuard.max_loss_pct}%</span> of account)</>}. {capitalGuard.note}
          </p>
        </div>
      )}

      <Mt5OrderTicket setup={setup} trade={trade} sizing={sizing} entryGuard={entryGuard} />

      {dynamicRisk && dynamicRisk.reasons?.length > 0 && (
        <div className="mt-4 rounded-lg p-3 text-xs" style={{ background: "rgba(245,185,66,0.08)", border: "1px solid rgba(245,185,66,0.25)" }}>
          <div className="font-semibold text-warn mb-1">Dynamic risk: {dynamicRisk.state} ({dynamicRisk.suggested_risk_pct}%)</div>
          <ul className="list-disc list-inside text-[var(--text-sec)] space-y-0.5">
            {dynamicRisk.reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      <p className="mt-3 text-[11px] text-[var(--text-muted)]">Levels are structure-based. Not a guarantee of profit. Trading involves substantial risk.</p>
    </Card>
  );
}
